/**
 * UI state for the Single-Row Details View — purely ephemeral per (table, pk).
 *
 * Keeps track of which related-data sections are expanded and which
 * row-cell category groups are collapsed *per row visit*. Resets when the
 * user navigates to a different row. Not session-persisted by design (the
 * spec calls this out — toolbar prefs live in `appStore` instead).
 */

import { create } from 'zustand';
import type { CategoryKey } from '../types/singleRowTypes';

/** Key used to scope per-row UI state. */
export function buildRowVisitKey(args: {
  readonly schema: string;
  readonly table: string;
  readonly pkHash: string;
}): string {
  return `${args.schema}.${args.table}#${args.pkHash}`;
}

type RowVisitState = {
  /** Expanded inbound-relation section keys (set semantics). */
  readonly expandedRelations: ReadonlyArray<string>;
  /** Collapsed cell-category group keys. */
  readonly collapsedGroups: ReadonlyArray<CategoryKey>;
};

type SingleRowStoreState = {
  readonly visits: Readonly<Record<string, RowVisitState>>;
  readonly toggleRelationExpanded: (rowKey: string, relationKey: string) => void;
  readonly setRelationExpanded: (rowKey: string, relationKey: string, expanded: boolean) => void;
  readonly expandAllRelations: (rowKey: string, allKeys: ReadonlyArray<string>) => void;
  readonly collapseAllRelations: (rowKey: string) => void;
  readonly toggleGroupCollapsed: (rowKey: string, group: CategoryKey) => void;
  readonly resetVisit: (rowKey: string) => void;
};

const EMPTY_VISIT: RowVisitState = {
  expandedRelations: [],
  collapsedGroups: [],
};

export const useSingleRowStore = create<SingleRowStoreState>((set) => ({
  visits: {},

  toggleRelationExpanded: (rowKey, relationKey) =>
    set((state) => {
      const current = state.visits[rowKey] ?? EMPTY_VISIT;
      const isExpanded = current.expandedRelations.includes(relationKey);
      const nextExpanded = isExpanded
        ? current.expandedRelations.filter((k) => k !== relationKey)
        : [...current.expandedRelations, relationKey];
      return {
        visits: {
          ...state.visits,
          [rowKey]: { ...current, expandedRelations: nextExpanded },
        },
      };
    }),

  setRelationExpanded: (rowKey, relationKey, expanded) =>
    set((state) => {
      const current = state.visits[rowKey] ?? EMPTY_VISIT;
      const has = current.expandedRelations.includes(relationKey);
      if (has === expanded) return state;
      const nextExpanded = expanded
        ? [...current.expandedRelations, relationKey]
        : current.expandedRelations.filter((k) => k !== relationKey);
      return {
        visits: {
          ...state.visits,
          [rowKey]: { ...current, expandedRelations: nextExpanded },
        },
      };
    }),

  expandAllRelations: (rowKey, allKeys) =>
    set((state) => {
      const current = state.visits[rowKey] ?? EMPTY_VISIT;
      return {
        visits: {
          ...state.visits,
          [rowKey]: { ...current, expandedRelations: [...allKeys] },
        },
      };
    }),

  collapseAllRelations: (rowKey) =>
    set((state) => {
      const current = state.visits[rowKey] ?? EMPTY_VISIT;
      return {
        visits: {
          ...state.visits,
          [rowKey]: { ...current, expandedRelations: [] },
        },
      };
    }),

  toggleGroupCollapsed: (rowKey, group) =>
    set((state) => {
      const current = state.visits[rowKey] ?? EMPTY_VISIT;
      const collapsed = current.collapsedGroups.includes(group);
      const next = collapsed
        ? current.collapsedGroups.filter((g) => g !== group)
        : [...current.collapsedGroups, group];
      return {
        visits: {
          ...state.visits,
          [rowKey]: { ...current, collapsedGroups: next },
        },
      };
    }),

  resetVisit: (rowKey) =>
    set((state) => {
      if (!(rowKey in state.visits)) return state;
      const nextVisits = { ...state.visits };
      delete (nextVisits as Record<string, RowVisitState>)[rowKey];
      return { visits: nextVisits };
    }),
}));

/** Convenience selector helpers for components. */
export function selectVisit(state: SingleRowStoreState, rowKey: string): RowVisitState {
  return state.visits[rowKey] ?? EMPTY_VISIT;
}
